import { create } from 'zustand';
import type { ManagerProfile } from '@/types';

export type AuthMethod = 'azure' | 'supabase';

interface AuthState {
  manager: ManagerProfile | null;
  /** Which identity provider the active session came from — used to log out correctly. */
  authMethod: AuthMethod | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  /** A valid session exists but it has no usable manager profile (not provisioned). */
  noProfile: boolean;
  error: string | null;
  setManager: (manager: ManagerProfile, method: AuthMethod) => void;
  setNoProfile: (method: AuthMethod) => void;
  setLoading: (isLoading: boolean) => void;
  setError: (error: string | null) => void;
  logout: () => void;
  getOrganizationIds: () => string[];
}

export const useAuthStore = create<AuthState>((set, get) => ({
  manager: null,
  authMethod: null,
  isAuthenticated: false,
  isLoading: true,
  noProfile: false,
  error: null,
  setManager: (manager, method) =>
    set({
      manager,
      authMethod: method,
      isAuthenticated: true,
      noProfile: false,
      isLoading: false,
      error: null,
    }),
  setNoProfile: (method) =>
    set({
      manager: null,
      authMethod: method,
      isAuthenticated: false,
      noProfile: true,
      isLoading: false,
    }),
  setLoading: (isLoading) => set({ isLoading }),
  setError: (error) => set({ error, isLoading: false }),
  logout: () =>
    set({
      manager: null,
      authMethod: null,
      isAuthenticated: false,
      noProfile: false,
      isLoading: false,
      error: null,
    }),
  getOrganizationIds: () => {
    const { manager } = get();
    return manager?.organization_ids ?? [];
  },
}));
